import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  OnDestroy,
  output,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { formatDate, NgClass } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  AbilityService,
  BreakpointService,
  DealStatus,
  DealStatusLabels,
  LocalStorageManagerService,
  rxResourceFixed,
  SkeletonComponent
} from '@zayan-erp/shared';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { MessageModule } from '@openng/optimus-ui/message';
import { FormsModule } from '@angular/forms';
import { toActionableReplacements, toExecutedTriggerProductIds } from '../../../utils/dsm-room-replacement-review.util';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';
import { DsmApsDealContextService } from '../../../services/dsm-aps-deal-context.service';
import { DsmApsService } from '../../../services/dsm-aps.service';
import { DsmExcludedBuilderReference } from '../../../models/dsm-deal-options.models';
import {
  DsmAddendumsQueryParam,
  DsmDealOption,
  DsmPrintAddendumOption
} from '../../../models/dsm-deal-options-view.models';
import {
  DsmRoomReplacementReviewExecution,
  DsmRoomReplacementReviewRoom
} from '../../../models/dsm-room-replacement-review.models';
import { DsmApsAddendumPreviewComponent } from '../dsm-aps-addendum-preview/dsm-aps-addendum-preview.component';
import { DsmApsAddendumOptionsComponent } from '../dsm-aps-addendum-options/dsm-aps-addendum-options.component';
import { DsmApsAddendumDetailsComponent } from '../dsm-aps-addendum-details/dsm-aps-addendum-details.component';
import { DsmApsAddendumOptionDetailsComponent } from '../dsm-aps-addendum-option-details/dsm-aps-addendum-option-details.component';
import { DsmAddAddendumModalComponent } from '../dsm-add-addendum-modal/dsm-add-addendum-modal.component';
import { DsmSendForSignOffModalComponent } from '../dsm-send-for-sign-off-modal/dsm-send-for-sign-off-modal.component';
import { DsmDealIncentivePoolBannerComponent } from '../dsm-deal-incentive-pool-banner/dsm-deal-incentive-pool-banner.component';
import { DsmLotIncentiveService } from '../../../services/dsm-lot-incentive.service';
import { DividerModule } from '@openng/optimus-ui/divider';
import { TagModule } from '@openng/optimus-ui/tag';
import { BadgeModule } from '@openng/optimus-ui/badge';
import { PopoverModule } from '@openng/optimus-ui/popover';

type DsmDealOptionWithExpandableView = DsmDealOption & { isExpanded: boolean };

@Component({
  selector: 'dsm-aps-addendums-view',
  standalone: true,
  host: { class: 'flex flex-col flex-1 overflow-hidden' },
  imports: [
    NgClass,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    FormsModule,
    TooltipModule,
    MessageModule,
    DsmApsAddendumPreviewComponent,
    DsmApsAddendumOptionsComponent,
    DsmApsAddendumDetailsComponent,
    DsmApsAddendumOptionDetailsComponent,
    DsmAddAddendumModalComponent,
    DsmSendForSignOffModalComponent,
    DsmDealIncentivePoolBannerComponent,
    DividerModule,
    TagModule,
    BadgeModule,
    PopoverModule,
    SkeletonComponent
  ],
  templateUrl: './dsm-aps-addendums-view.component.html',
  styleUrl: './dsm-aps-addendums-view.component.scss'
})
export class DsmApsAddendumsViewComponent implements OnDestroy {
  dealId = input.required<string>();

  /** Raised once an envelope has been created, so the host can route to Sign-off Tracking. */
  sentForSignOff = output<void>();
  /** Raised with the envelope id when the agent wants to inspect a blocking envelope. */
  viewEnvelope = output<string>();

  readonly #dealOptionsService = inject(DsmDealOptionsService);
  readonly #dealContext = inject(DsmApsDealContextService);
  readonly #dsmApsService = inject(DsmApsService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #breakpointService = inject(BreakpointService);
  readonly #lotIncentiveService = inject(DsmLotIncentiveService);
  readonly #localStorageManager = inject(LocalStorageManagerService);
  readonly #abilityService = inject(AbilityService);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #toastr = inject(ToastrService);
  readonly #injector = inject(Injector);

  isMobile = toSignal(this.#breakpointService.isMobile$, { initialValue: false });

  actionsGroupRef = viewChild<ElementRef<HTMLDivElement>>('actionsGroup');
  readonly optionDetails = viewChild(DsmApsAddendumOptionDetailsComponent);

  addAddendumDialogVisible = signal(false);
  printAddendumDialogVisible = signal(false);
  sendForSignOffDialogVisible = signal(false);
  printOptionSelections = signal<Record<string, boolean>>({});

  dealResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.dealId) return of(null);
      return this.#dsmApsService.getDealHeader(params.dealId, false);
    }
  });

  contractsResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.dealId) return of(null);
      return this.#dsmApsService.getBuyerContracts(params.dealId, false);
    }
  });

  // Room Replacement Rules: review data drives the mismatch banners + "Mismatch Locations" badges
  // rendered inline in this same Addendums list (no separate Review List tab).
  reviewResource = rxResourceFixed({
    params: () => ({ dealId: this.dealId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (!params.dealId) return of(null);
      return this.#dsmApsService.getRoomReplacementReview(params.dealId, false);
    }
  });

  allExecutions = computed<DsmRoomReplacementReviewExecution[]>(() => this.reviewResource.value()?.replacements ?? []);
  hasExecutedRule = computed(() => this.allExecutions().length > 0);

  appliedRulesLabel = computed(() =>
    [...this.allExecutions()]
      .sort((a, b) => a.executedAt.localeCompare(b.executedAt))
      .map(
        execution =>
          `${execution.triggerProductCode || execution.triggerProductDescription || 'A location rule'} · ${formatDate(execution.executedAt, 'MM/dd/yyyy', 'en-US')}`
      )
      .join(', ')
  );
  executedRulesBannerDismissed = signal(false);

  acknowledgedExecutionIds = signal<Set<string>>(
    this.#localStorageManager.getAcknowledgedRoomReplacementExecutionIds('dsm')
  );

  pendingDisclosureExecutions = computed<DsmRoomReplacementReviewExecution[]>(() =>
    this.allExecutions().filter(execution => !this.acknowledgedExecutionIds().has(execution.executionId))
  );

  currentDisclosure = computed<DsmRoomReplacementReviewExecution | null>(
    () => this.pendingDisclosureExecutions()[0] ?? null
  );

  currentDisclosureLabel = computed<string | null>(() => {
    const execution = this.currentDisclosure();
    return execution ? this.executionLabel(execution) : null;
  });

  acknowledgeCurrentDisclosure(): void {
    const execution = this.currentDisclosure();
    if (!execution) return;
    this.#localStorageManager.acknowledgeRoomReplacementExecution(execution.executionId, 'dsm');
    this.acknowledgedExecutionIds.update(ids => new Set(ids).add(execution.executionId));
  }

  replacements = computed<DsmRoomReplacementReviewExecution[]>(() =>
    toActionableReplacements(this.reviewResource.value())
  );

  private mismatchedProductIdsByAddendum = computed<Map<string, Set<string>>>(() => {
    const map = new Map<string, Set<string>>();
    for (const line of this.reviewResource.value()?.mismatchedLines ?? []) {
      const set = map.get(line.addendumId) ?? new Set<string>();
      set.add(line.addendumProductId);
      map.set(line.addendumId, set);
    }
    return map;
  });

  private static readonly emptyMismatchedProductIds: ReadonlySet<string> = new Set();

  mismatchedProductIdsFor(addendumId: string): ReadonlySet<string> {
    return (
      this.mismatchedProductIdsByAddendum().get(addendumId) ?? DsmApsAddendumsViewComponent.emptyMismatchedProductIds
    );
  }

  hasMismatch(addendumId: string): boolean {
    return this.mismatchedProductIdsFor(addendumId).size > 0;
  }

  readonly canViewPendingLocationRules = computed(() =>
    this.#abilityService.ability.can('dsm-view-pending-location-rules', 'aps')
  );

  executedTriggerProductIds = computed(() => toExecutedTriggerProductIds(this.reviewResource.value()));

  executionLabel(execution: DsmRoomReplacementReviewExecution): string {
    return execution.triggerProductDescription || execution.triggerProductCode || 'A room replacement rule';
  }

  roomNames(rooms: DsmRoomReplacementReviewRoom[]): string {
    return (
      rooms
        .map(room => room.locationName)
        .filter(Boolean)
        .join(', ') || '—'
    );
  }

  effectiveDeal = computed(() => this.#dealContext.currentDeal() ?? this.dealResource.value() ?? null);
  effectiveDealId = computed(() => this.effectiveDeal()?.id ?? '');
  effectiveLotId = computed(() => this.effectiveDeal()?.lot?.id ?? '');
  effectiveModelId = computed(() => this.effectiveDeal()?.model?.id ?? '');
  effectiveElevationId = computed(() => this.effectiveDeal()?.elevation?.id ?? '');
  isUnderContract = computed(() => this.effectiveDeal()?.dealStatus?.name === 'Under Contract');
  /**
   * A cancelled deal's addendums are read-only. Its clean-up has already run, so anything added or
   * approved here would stay active on the lot for the next buyer, who never sees it on their own page.
   */
  isDealCancelled = computed(() => this.effectiveDeal()?.dealStatus?.name === DealStatusLabels[DealStatus.Cancelled]);
  effectiveTargetDate = computed(() => this.contractsResource.value()?.targetClosingDate ?? null);

  selectedAddendumId = signal<string | null>(null);
  selectedProductId = signal<string | null>(null);
  selectedAddendumIdsForPrint = signal<string[]>([]);
  selectedKitProductId: string | null = null;

  dealOptionsHistory: DsmDealOptionWithExpandableView[] = [];

  constructor() {
    effect(() => {
      const deal = this.dealResource.value();
      if (deal) {
        this.#dealContext.currentDeal.set(deal);
      }
    });

    effect(() => {
      if (this.#dealContext.reloadDeal()) {
        this.dealResource.reload();
        this.contractsResource.reload();
        this.reviewResource.reload();
        this.#dealContext.reloadDeal.set(false);
      }
    });

    effect(() => {
      const lotId = this.effectiveLotId();
      const dealId = this.effectiveDealId();
      if (lotId && dealId) {
        this.#lotIncentiveService.fetchLotIncentive(lotId, dealId);
      }
    });

    effect(() => {
      this.#dealContext.hasAddendumsForPrint.set(!this.isDealOptionsLoading() && this.dealsOptions().length > 0);
    });
    effect(() => {
      this.#dealContext.isAllAddendumsSelectedForPrint.set(this.isAllAddendumsSelectedForPrint());
    });
    effect(() => {
      this.#dealContext.isSelectAllForPrintIndeterminate.set(this.isSelectAllForPrintIndeterminate());
    });
    this.#dealContext.toggleSelectAllForPrint.set(checked => this.onSelectAllPrintChange(checked));

    afterNextRender(() => {
      const el = this.actionsGroupRef()?.nativeElement;
      if (!el) return;
      const resizeObserver = new ResizeObserver(entries => {
        this.#dealContext.addendumsActionsWidth.set(entries[0]?.contentRect.width ?? 0);
      });
      resizeObserver.observe(el);
      this.#destroyRef.onDestroy(() => resizeObserver.disconnect());
    });

    this.#destroyRef.onDestroy(() => {
      this.#lotIncentiveService.resetLotIncentive();
      this.#dealContext.hasAddendumsForPrint.set(false);
      this.#dealContext.toggleSelectAllForPrint.set(null);
      this.#dealContext.addendumsActionsWidth.set(0);
      this.#dealContext.showConflictsOnly.set(false);
    });
  }

  dealsOptionsRxResource = rxResourceFixed({
    params: () => ({ dealId: this.effectiveDealId(), lotId: this.effectiveLotId() }),
    defaultValue: [],
    stream: ({ params }) => {
      if (!params.lotId || !params.dealId) return of([]);
      return this.#dealOptionsService.getAddendums(params.lotId, params.dealId, false);
    }
  });

  /** True while addendums are fetching (including reload). Drives list skeletons and keeps empty-gated actions disabled. */
  isDealOptionsLoading = computed(() => this.dealsOptionsRxResource.isLoading());

  excludedFromSigningResource = rxResourceFixed({
    defaultValue: [] as DsmExcludedBuilderReference[],
    stream: () => this.#dealOptionsService.excludeFromSigningPreview()
  });

  printAddendumOptionsResource = rxResourceFixed({
    params: () => ({ visible: this.printAddendumDialogVisible() }),
    defaultValue: [] as DsmPrintAddendumOption[],
    stream: ({ params }) => {
      if (!params.visible) return of([]);
      return this.#dealOptionsService.getPrintAddendumOptions();
    }
  });

  dealsOptions = linkedSignal<DsmDealOptionWithExpandableView[]>(() => {
    const options = this.dealsOptionsRxResource.value() ?? [];
    return options.map(option => {
      const isPreviouslyExpanded = this.dealOptionsHistory.find(history => history.id === option.id)?.isExpanded;
      const hasNoProducts = option.products.length === 0;
      if (isPreviouslyExpanded === false) {
        return { ...option, isExpanded: false } as DsmDealOptionWithExpandableView;
      }
      return {
        ...option,
        isExpanded: isPreviouslyExpanded || hasNoProducts
      } as DsmDealOptionWithExpandableView;
    });
  });

  onDealsOptionsSet = effect(() => {
    this.dealOptionsHistory = this.dealsOptions();
  });

  /** Rendered inline here on desktop; on mobile it moves into the tabs strip (see APSOptionsComponent). */
  showConflictsOnly = this.#dealContext.showConflictsOnly;
  visibleDealsOptions = computed<DsmDealOptionWithExpandableView[]>(() => {
    const options = this.dealsOptions();
    return this.showConflictsOnly() ? options.filter(option => this.hasMismatch(option.id)) : options;
  });

  onPrintLookupOptionsSet = effect(() => {
    this.printAddendumOptionsResource.value();
    this.onPrintOptionsLoaded();
  });

  private readonly syncPrintSelectionWithOptions = effect(() => {
    const options = this.dealsOptions();
    const validIds = new Set(options.map(o => o.id));
    this.selectedAddendumIdsForPrint.update(sel => {
      const next = sel.filter(id => validIds.has(id));
      return next.length === sel.length && next.every((id, i) => id === sel[i]) ? sel : next;
    });
  });

  isAllAddendumsSelectedForPrint = computed(() => {
    const visibleCount = this.visibleDealsOptions().length;
    return visibleCount > 0 && this.visibleSelectedIds().length === visibleCount;
  });

  /** Selected ids that are currently visible, so hidden (filtered-out) selections never leak into print/sign-off. */
  visibleSelectedIds = computed<string[]>(() => {
    const visibleIds = new Set(this.visibleDealsOptions().map(o => o.id));
    return this.selectedAddendumIdsForPrint().filter(id => visibleIds.has(id));
  });

  /**
   * Addendums eligible for DSM print and sign-off. Schedule PE / Sales CR are dropped via
   * GET BuilderReference/excluded — the same list send-for-sign-off uses.
   */
  addendumsForSignOff = computed<DsmDealOption[]>(() => {
    if (this.excludedFromSigningResource.isLoading() || this.excludedFromSigningResource.error()) {
      return [];
    }
    const excludedIds = new Set((this.excludedFromSigningResource.value() ?? []).map(ref => ref.id));
    const options = this.visibleDealsOptions().filter(option => !excludedIds.has(option.builderReference?.id));
    const selectedIds = this.visibleSelectedIds();
    if (!selectedIds.length) return options;
    return options.filter(option => selectedIds.includes(option.id));
  });

  printAddendumsButtonLabel = computed(() => {
    const hasSelection = this.visibleSelectedIds().length > 0;
    const count = this.addendumsForSignOff().length;
    if (hasSelection && count > 0) return `Print ${count} addendums`;
    return this.showConflictsOnly() ? 'Print all conflicts' : 'Print all addendums';
  });

  sendForSignOffButtonLabel = computed(() => {
    const hasSelection = this.visibleSelectedIds().length > 0;
    const count = this.addendumsForSignOff().length;
    return hasSelection && count > 0 ? `Send ${count} for Sign-off` : 'Send for Sign-off';
  });

  hasBlockedAddendumsForSignOff = computed(() =>
    this.addendumsForSignOff().some(addendum => !!addendum.signOff?.dealDocumentId)
  );

  isSelectAllForPrintIndeterminate = computed(() => {
    const visibleCount = this.visibleDealsOptions().length;
    const selected = this.visibleSelectedIds().length;
    return visibleCount > 0 && selected > 0 && selected < visibleCount;
  });

  selectedAddendum = computed(
    () => this.dealsOptions().find(addendum => addendum.id === this.selectedAddendumId()) ?? null
  );

  showAddendumDetails = computed(() => this.selectedAddendum() !== null && this.selectedProductId() === null);
  showOptionDetails = computed(() => this.selectedAddendum() !== null && this.selectedProductId() !== null);

  readonly #queryParamMap = toSignal(this.#route.queryParamMap, { requireSync: true });

  #appliedParamsKey: string | null = null;

  private readonly selectFromUrl = effect(() => {
    const params = this.#queryParamMap();
    const addendumId = params.get(DsmAddendumsQueryParam.Addendum);
    const optionId = params.get(DsmAddendumsQueryParam.Option);

    const hasLoaded =
      !!this.effectiveDealId() && !!this.effectiveLotId() && this.dealsOptionsRxResource.status() === 'resolved';
    if (!hasLoaded) return;

    const addendums = this.dealsOptionsRxResource.value() ?? [];
    const urlKey = this.selectionKey(addendumId, optionId);
    const isFromOutside = urlKey !== this.#appliedParamsKey;

    const option = optionId ? this.findOption(addendums, optionId) : null;
    const isKnownAddendum = !!addendumId && addendums.some(addendum => addendum.id === addendumId);
    const resolvedAddendumId = option?.addendumId ?? (isKnownAddendum ? addendumId : null);
    const resolvedOptionId = option ? optionId : null;

    // Our own selection vanishing on reload (e.g. the option was just deleted) falls back quietly.
    if (isFromOutside) {
      if (optionId && !option) this.#toastr.warning('Option not found on this deal.');
      else if (addendumId && !isKnownAddendum && !option) this.#toastr.warning('Addendum not found on this deal.');
    }

    this.selectedAddendumId.set(resolvedAddendumId);
    this.selectedProductId.set(resolvedOptionId);
    this.selectedKitProductId = option?.isKitLinked ? optionId : null;

    if (this.selectionKey(resolvedAddendumId, resolvedOptionId) !== urlKey) {
      this.writeSelectionToUrl(resolvedAddendumId, resolvedOptionId);
    } else {
      this.#appliedParamsKey = urlKey;
    }

    if (isFromOutside && resolvedAddendumId) {
      untracked(() => this.revealSelection(resolvedAddendumId, optionId));
    }
  });

  private selectionKey(addendumId: string | null, optionId: string | null): string {
    return `${addendumId ?? ''}|${optionId ?? ''}`;
  }

  /** Expands the linked addendum's card and scrolls the addendum (or its option row) into view. */
  private revealSelection(addendumId: string, optionId: string | null): void {
    if (this.showConflictsOnly() && !this.hasMismatch(addendumId)) {
      this.showConflictsOnly.set(false);
    }
    this.dealsOptions.update(addendums =>
      addendums.some(addendum => addendum.id === addendumId && !addendum.isExpanded)
        ? addendums.map(addendum => (addendum.id === addendumId ? { ...addendum, isExpanded: true } : addendum))
        : addendums
    );
    afterNextRender(
      () => {
        const target =
          (optionId && document.getElementById(`addendum-option-${optionId}`)) ||
          document.getElementById(`addendum-${addendumId}`);
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      { injector: this.#injector }
    );
  }

  private findOption(
    addendums: DsmDealOption[],
    optionId: string
  ): { addendumId: string; isKitLinked: boolean } | null {
    for (const addendum of addendums) {
      for (const product of addendum.products) {
        if (product.id === optionId) return { addendumId: addendum.id, isKitLinked: false };
        if (product.linkedProducts?.some(linked => linked.id === optionId)) {
          return { addendumId: addendum.id, isKitLinked: true };
        }
      }
    }
    return null;
  }

  onOptionChanges(): void {
    this.dealsOptionsRxResource.reload();
    this.reviewResource.reload();
  }

  onOptionCardChanges(): void {
    this.onOptionChanges();
    this.optionDetails()?.productRxResource.reload();
  }

  openAddAddendumDialog(): void {
    this.addAddendumDialogVisible.set(true);
  }

  onAddendumSaved(): void {
    this.addAddendumDialogVisible.set(false);
    this.dealsOptionsRxResource.reload();
  }

  openSendForSignOffDialog(): void {
    if (this.addendumsForSignOff().length === 0) return;
    this.sendForSignOffDialogVisible.set(true);
  }

  onSentForSignOff(): void {
    this.sendForSignOffDialogVisible.set(false);
    this.selectedAddendumIdsForPrint.set([]);
    this.dealsOptionsRxResource.reload();
    this.sentForSignOff.emit();
  }

  onViewEnvelope(envelopeId: string): void {
    this.sendForSignOffDialogVisible.set(false);
    this.viewEnvelope.emit(envelopeId);
  }

  closeMobileDetails(): void {
    this.selectedAddendumId.set(null);
    this.selectedProductId.set(null);
    this.writeSelectionToUrl(null, null);
  }

  onAddendumSelect(optionId: string): void {
    this.selectedAddendumId.set(optionId);
    this.selectedProductId.set(null);
    this.writeSelectionToUrl(optionId, null);
  }

  onAddendumProductSelect(addendumId: string, productId: string, isKitLinkedProduct = false): void {
    this.selectedAddendumId.set(addendumId);
    this.selectedProductId.set(productId);
    this.selectedKitProductId = isKitLinkedProduct ? productId : null;
    this.writeSelectionToUrl(addendumId, productId);
  }

  private writeSelectionToUrl(addendumId: string | null, optionId: string | null): void {
    this.#appliedParamsKey = this.selectionKey(addendumId, optionId);
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { [DsmAddendumsQueryParam.Addendum]: addendumId, [DsmAddendumsQueryParam.Option]: optionId },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  isAddendumSelectedForPrint(id: string): boolean {
    return this.selectedAddendumIdsForPrint().includes(id);
  }

  onAddendumPrintToggle(addendumId: string, checked: boolean): void {
    this.selectedAddendumIdsForPrint.update(ids => {
      if (checked) {
        return ids.includes(addendumId) ? ids : [...ids, addendumId];
      }
      return ids.filter(i => i !== addendumId);
    });
  }

  onSelectAllPrintChange(checked: boolean): void {
    const visibleIds = new Set(this.visibleDealsOptions().map(o => o.id));
    this.selectedAddendumIdsForPrint.update(sel => {
      if (!checked) return sel.filter(id => !visibleIds.has(id));
      const merged = new Set(sel);
      visibleIds.forEach(id => merged.add(id));
      return [...merged];
    });
  }

  openPrintAddendumDialog(): void {
    if (this.addendumsForSignOff().length === 0) return;
    this.printAddendumDialogVisible.set(true);
    this.printAddendumOptionsResource.reload();
  }

  onPrintDialogVisibilityChange(visible: boolean): void {
    this.printAddendumDialogVisible.set(visible);
    if (visible) return;
    this.printOptionSelections.set({});
  }

  onPrintOptionsLoaded(): void {
    if (!this.printAddendumDialogVisible()) return;
    const options = this.printAddendumOptionsResource.value() ?? [];
    const defaults = options.reduce<Record<string, boolean>>((acc, option) => {
      acc[option.name] = !!option.isDefault;
      return acc;
    }, {});
    this.printOptionSelections.set(defaults);
  }

  isPrintOptionSelected(optionName: string): boolean {
    return !!this.printOptionSelections()[optionName];
  }

  onPrintOptionToggle(optionName: string, optionId: number, selected: boolean): void {
    if (optionId === 13)
      this.printOptionSelections.update(current => ({
        ...current,
        ApprovedAddendums: selected,
        PendingAddendums: selected
      }));
    this.printOptionSelections.update(current => ({ ...current, [optionName]: selected }));
  }

  submitPrintAddendums(): void {
    const dealId = this.effectiveDealId();
    const addendums = this.addendumsForSignOff();
    if (!dealId || addendums.length === 0) return;
    const addendumIds = addendums.map(addendum => addendum.id);
    const selectedOptionIds = this.buildSelectedOptionIds();
    if (!selectedOptionIds.length) return;

    const lotNo = this.effectiveDeal()?.lot?.lotNo;
    this.#dealOptionsService
      .downloadAddendumsPdf(dealId, addendumIds, selectedOptionIds, `${lotNo}-Addendum-${+new Date()}.pdf`)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.onPrintDialogVisibilityChange(false));
  }

  private buildSelectedOptionIds(): number[] {
    const options = this.printAddendumOptionsResource.value() ?? [];
    const selections = this.printOptionSelections();
    return options.filter(option => !!selections[option.name]).map(option => option.id);
  }

  ngOnDestroy(): void {
    this.#lotIncentiveService.resetLotIncentive();
  }
}
